import { EvComboboxOption } from '@evinced-private/ui-common';

import { formatDate } from '../../../helpers/DateFormatHelper';
import {
	CollectionTypes,
	ICollection,
	ICollectionUpdatedBy,
	IEditCollectionFormValues
} from '../../../interfaces/Collection';
import { IProperty } from '../../../interfaces/Property';

const NAME_REQUIRED_ERROR_MESSAGE = 'Enter a collection name';
const NAME_MAX_LENGTH_ERROR_MESSAGE = 'Name can’t be longer than 100 characters';
const INVALID_NAME_CHARACTERS_ERROR_MESSAGE = 'Collection name can’t contain special characters';
const INVALID_DESCRIPTION_CHARACTERS_ERROR_MESSAGE =
	'Description can’t contain special characters except hyphens (-), underscores (_), periods (.), and commas (,)';
const DESCRIPTION_MAX_LENGTH_ERROR_MESSAGE = 'Description can’t be longer than 300 characters';
export const COLLECTION_TYPE_ERROR_MESSAGE = 'Only admins can create public collections';

const allowedNameCharactersRegex = /^[A-Za-z0-9\s]*$/;
const allowedDescriptionCharactersRegex = /^[A-Za-z0-9\s\-_.,]*$/;

export enum COLLECTION_FORM_FIELDS {
	NAME = 'name',
	COLLECTION_TYPE = 'collectionType',
	SELECTED_PROPERTIES = 'selectedProperties',
	DESCRIPTION = 'description'
}

export type IFormErrors = {
	[key in keyof IEditCollectionFormValues]: string[];
};

interface IValidateFieldParams {
	errors: IFormErrors;
	condition: boolean;
	errorMessage: string;
}

export const formEmptyState: IEditCollectionFormValues = {
	[COLLECTION_FORM_FIELDS.NAME]: '',
	[COLLECTION_FORM_FIELDS.COLLECTION_TYPE]: CollectionTypes.USER,
	[COLLECTION_FORM_FIELDS.SELECTED_PROPERTIES]: [],
	[COLLECTION_FORM_FIELDS.DESCRIPTION]: ''
};

export const formEmptyErrorState: IFormErrors = {
	[COLLECTION_FORM_FIELDS.NAME]: [],
	[COLLECTION_FORM_FIELDS.COLLECTION_TYPE]: [],
	[COLLECTION_FORM_FIELDS.SELECTED_PROPERTIES]: [],
	[COLLECTION_FORM_FIELDS.DESCRIPTION]: []
};

export const mapPropertiesToOptions = (properties: IProperty[]): EvComboboxOption[] => {
	return (
		properties?.map((property) => ({
			label: property.name,
			value: property.id
		})) ?? formEmptyState.selectedProperties
	);
};

export const mapCollectionPropertyIdsToOptions = (
	collectionPropertyIds: string[],
	properties: IProperty[]
): EvComboboxOption[] => {
	return (
		collectionPropertyIds?.map((id) => {
			const property = properties.find((property) => property.id === id);

			return {
				label: property?.name ?? id,
				value: property?.id ?? id
			};
		}) ?? []
	);
};

const validateField = (
	field: keyof IEditCollectionFormValues,
	{ errors, condition, errorMessage }: IValidateFieldParams
): void => {
	if (condition) {
		errors[field].push(errorMessage);
	} else {
		errors[field] = errors[field].filter((error) => error !== errorMessage);
	}
};

export const validateCollectionForm = (
	values: IEditCollectionFormValues,
	isAdminUser: boolean
): IFormErrors => {
	const errors = structuredClone(formEmptyErrorState);

	// Name validation
	validateField(COLLECTION_FORM_FIELDS.NAME, {
		errors,
		condition: !values.name,
		errorMessage: NAME_REQUIRED_ERROR_MESSAGE
	});
	validateField(COLLECTION_FORM_FIELDS.NAME, {
		errors,
		condition: values.name.length > 100,
		errorMessage: NAME_MAX_LENGTH_ERROR_MESSAGE
	});
	validateField(COLLECTION_FORM_FIELDS.NAME, {
		errors,
		condition: !allowedNameCharactersRegex.test(values.name),
		errorMessage: INVALID_NAME_CHARACTERS_ERROR_MESSAGE
	});

	// Type validation
	validateField(COLLECTION_FORM_FIELDS.COLLECTION_TYPE, {
		errors,
		condition: values.collectionType === CollectionTypes.TENANT && !isAdminUser,
		errorMessage: COLLECTION_TYPE_ERROR_MESSAGE
	});

	// Description validation
	validateField(COLLECTION_FORM_FIELDS.DESCRIPTION, {
		errors,
		condition: values.description.length > 300,
		errorMessage: DESCRIPTION_MAX_LENGTH_ERROR_MESSAGE
	});
	validateField(COLLECTION_FORM_FIELDS.DESCRIPTION, {
		errors,
		condition: !allowedDescriptionCharactersRegex.test(values.description),
		errorMessage: INVALID_DESCRIPTION_CHARACTERS_ERROR_MESSAGE
	});

	return errors;
};

export const isFormValid = (formErrors: IFormErrors): boolean => {
	return Object.values(formErrors).every((errors) => errors.length === 0);
};

const getDisplayName = (updatedBy: ICollectionUpdatedBy): string => {
	let displayName = '';

	if (!updatedBy) {
		return '';
	}

	if (updatedBy.name && updatedBy.surname) {
		displayName = `${updatedBy.name} ${updatedBy.surname}`;
	} else if (updatedBy.email) {
		displayName = updatedBy.email;
	}

	return displayName;
};

export const getLastEdited = (collection: ICollection): string => {
	const { updatedTime, updatedBy, type } = collection || {};

	const displayName = getDisplayName(updatedBy);

	if (!updatedTime || !updatedBy || !displayName || type === CollectionTypes.USER) {
		return '';
	}

	const displayDate = formatDate(updatedTime);

	return `Last edited by ${displayName} on ${displayDate}`;
};
